/**
 * Mention Autocomplete Component
 * Displays dropdown with user suggestions when @ is typed
 */

import React, { useEffect, useRef, useMemo } from 'react';
import { UserOption } from '@/utils/mentionParser';

interface MentionAutocompleteProps {
  users: UserOption[];
  query: string;
  selectedIndex: number;
  onSelectUser: (user: UserOption) => void;
  onClose: () => void;
  position: { top: number; left: number };
  visible: boolean;
}

const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  users,
  query,
  selectedIndex,
  onSelectUser,
  onClose,
  position,
  visible,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && dropdownRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [visible, onClose]);

  // Filter users based on query
  const filteredUsers = useMemo(() => {
    if (!query.trim()) return users;
    const lowerQuery = query.toLowerCase().trim();
    return users.filter((user) => {
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      return name.includes(lowerQuery) || email.includes(lowerQuery);
    });
  }, [users, query]);

  if (!visible) {
    return null;
  }

  if (filteredUsers.length === 0) {
    return (
      <div
        ref={dropdownRef}
        className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        style={{ top: `${position.top}px`, left: `${position.left}px`, minWidth: '200px' }}
      >
        <div className="px-4 py-2 text-sm text-gray-500">No users found</div>
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
      style={{ minWidth: '200px' }}
    >
      {filteredUsers.map((user, index) => (
        <div
          key={user.id}
          ref={index === selectedIndex ? selectedItemRef : null}
          onClick={() => onSelectUser(user)}
          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 transition-colors ${
            index === selectedIndex ? 'bg-gray-100' : ''
          }`}
        >
          <span className="text-sm text-gray-900">
            <span className="text-gray-500">@</span> {user.name}
          </span>
        </div>
      ))}
    </div>
  );
};

export default MentionAutocomplete;

